import type { ReactNode } from "react";
import { BellRing, Boxes, ShoppingBag } from "lucide-react";
import { StoreLogo, TheAdamMark } from "@/components/brand";

const FEATURES = [
  { icon: ShoppingBag, title: "Birleşik sipariş havuzu", text: "IdeaSoft ve Trendyol siparişleri, kargo ve iadeler tek ekranda." },
  { icon: Boxes, title: "Platform bazlı katalog", text: "Stok ve fiyatı her kanal için ayrı izleyin, tek tıkla güncelleyin." },
  { icon: BellRing, title: "Anlık bildirim", text: "Yeni sipariş, iptal ve tükenen stok masaüstünüze düşer." },
];

/** Giriş sayfaları: solda marka ve kimlik, sağda form kartı. */
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="grid min-h-dvh bg-background lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[oklch(0.22_0.05_262)] via-sidebar to-[oklch(0.13_0.03_262)] text-sidebar-foreground lg:flex lg:flex-col lg:p-12 xl:p-16">
        <div aria-hidden className="pointer-events-none absolute -top-40 -left-40 size-[36rem] rounded-full bg-accent/12 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent" />

        <div className="relative flex flex-col items-start">
          <TheAdamMark tone="onDark" className="h-8 self-start" />
          <span className="mt-2 pl-[0.42em] text-[11px] font-semibold tracking-[0.42em] text-sidebar-muted uppercase">E-Commerce</span>
        </div>

        <div className="relative z-10 my-auto max-w-lg py-12">
          <p className="text-xs font-semibold tracking-[0.3em] text-accent uppercase">Çalışma alanı</p>
          <h1 className="sr-only">Kadri Yakışıklı Saat</h1>
          <StoreLogo tone="onDark" className="mt-5 h-20 self-start object-contain object-left" />
          <p className="mt-8 text-3xl leading-tight font-semibold tracking-tight text-white xl:text-[2.1rem]">Mağazanızın tüm satış kanalları, tek panelde.</p>
          <ul className="mt-10 space-y-5">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex gap-4">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-accent/25 bg-accent/10 text-accent">
                  <f.icon className="size-5" aria-hidden />
                </span>
                <span>
                  <span className="block font-semibold text-white">{f.title}</span>
                  <span className="mt-0.5 block text-sm leading-relaxed text-sidebar-foreground/70">{f.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-sidebar-muted">© {new Date().getFullYear()} THE ADAM · Kadri Yakışıklı Saat</p>
      </aside>

      <main className="relative flex flex-col items-center justify-center px-4 py-10 sm:px-8">
        <div aria-hidden className="pointer-events-none absolute top-0 right-0 size-80 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative w-full max-w-[440px]">
          <div className="mb-8 flex flex-col items-center gap-4 lg:hidden">
            <TheAdamMark className="h-7" />
            <StoreLogo className="h-14" />
          </div>
          <div className="rounded-3xl border bg-card p-8 shadow-2xl shadow-black/[0.06] sm:p-10">
            <h2 className="text-[1.65rem] leading-tight font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
            <div className="mt-8">{children}</div>
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">Hesabınız yoksa veya erişim sorunu yaşıyorsanız çalışma alanı yöneticinize başvurun.</p>
        </div>
      </main>
    </div>
  );
}
